import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";
import useTranslation from "@/hooks/use_translation";
import SearchIcon from "@/sprout/icons/search";
import { TextInput } from "@/sprout/text_input";
import { IconLocationInput } from "@components/ui/location_input";
import { Location } from "@components/ui/location_input";
import "./search_bar.scss";
import { PrimaryButton } from "@sprout/buttons";

export type Props = {
  onSearch: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  location: Location | null;
  onLocationChange: (location: Location | null) => void;
  disabled: boolean;
  granularLocationSearchEnabled: boolean;
};

export const SearchBar = ({
  onSearch,
  query,
  onQueryChange,
  location,
  onLocationChange,
  disabled,
  granularLocationSearchEnabled,
}: Props) => {
  const { t } = useTranslation("jobSearch");

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.metaKey && event.key === "k" && inputRef.current) {
        inputRef.current.focus();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  const handleEnterKey = (event: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  return (
    <div className="search-bar">
      <div>
        <TextInput
          ref={inputRef}
          placeholder={t("searchPlaceholder")}
          onChange={(e) => onQueryChange(e.currentTarget.value || "")}
          onKeyDown={handleEnterKey}
          maxLength={255}
          value={query}
          narrow={true}
          icon={<SearchIcon />}
          onClear={() => onQueryChange("")}
        />
      </div>
      <div>
        <IconLocationInput
          onChange={onLocationChange}
          value={location}
          granularLocationSearchEnabled={granularLocationSearchEnabled}
        />
      </div>

      <PrimaryButton onClick={onSearch} disabled={disabled}>
        {t("search")}
      </PrimaryButton>
    </div>
  );
};
